import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowRightLeft, Plus, Package, Check, X, Clock, Search,
  ArrowRight, Store, AlertTriangle, Truck
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', label: 'Pending' },
  approved: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', label: 'Approved' },
  rejected: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Rejected' },
};

export default function InventoryTransfers() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [stores, setStores] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // New Transfer Dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [fromStore, setFromStore] = useState('');
  const [toStore, setToStore] = useState('');
  const [transferItems, setTransferItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Review Dialog
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewTransfer, setReviewTransfer] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [storeRes, transferRes, productRes] = await Promise.all([
        axios.get(`${API_URL}/api/stores`, { headers }),
        axios.get(`${API_URL}/api/stores/transfers/history`, { headers }),
        axios.get(`${API_URL}/api/pos/products/search?limit=200`, { headers }),
      ]);
      setStores(storeRes.data);
      setTransfers(transferRes.data);
      setProducts(productRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredTransfers = filter === 'all'
    ? transfers
    : transfers.filter(t => t.status === filter);

  const pendingCount = transfers.filter(t => t.status === 'pending').length;

  // Add product to transfer
  const addItem = (product) => {
    if (transferItems.find(i => i.product_id === product.id)) {
      toast.error('Product already added');
      return;
    }
    setTransferItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      quantity: 1,
      stock: product.stock_quantity || 0,
    }]);
    setProductSearch('');
  };

  const updateItemQty = (productId, qty) => {
    setTransferItems(prev => prev.map(i =>
      i.product_id === productId ? { ...i, quantity: Math.max(1, Math.min(qty, i.stock)) } : i
    ));
  };

  const removeItem = (productId) => {
    setTransferItems(prev => prev.filter(i => i.product_id !== productId));
  };

  const handleSubmitTransfer = async () => {
    if (!fromStore || !toStore) { toast.error('Select both stores'); return; }
    if (fromStore === toStore) { toast.error('Source and destination must be different'); return; }
    if (transferItems.length === 0) { toast.error('Add at least one product'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/stores/transfer`, {
        from_store_id: fromStore,
        to_store_id: toStore,
        items: transferItems.map(i => ({ product_id: i.product_id, product_name: i.product_name, quantity: i.quantity })),
        notes,
      }, { headers });
      toast.success('Transfer request created — pending approval');
      setShowNewDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReview = async (action) => {
    if (!reviewTransfer) return;
    setReviewing(true);
    try {
      await axios.post(`${API_URL}/api/stores/transfer/${reviewTransfer.id}/action`, {
        action,
        notes: reviewNotes,
      }, { headers });
      toast.success(`Transfer ${action === 'approve' ? 'approved' : 'rejected'}`);
      setShowReviewDialog(false);
      setReviewTransfer(null);
      setReviewNotes('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} transfer`);
    } finally {
      setReviewing(false);
    }
  };

  const resetForm = () => {
    setFromStore(''); setToStore(''); setTransferItems([]); setNotes(''); setProductSearch('');
  };

  const searchResults = productSearch.length >= 2
    ? products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase())).slice(0, 8)
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="transfers-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-transfers-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ArrowRightLeft className="h-6 w-6 text-blue-500" />
            Inventory Transfers
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Transfer stock between stores with approval workflow</p>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <Badge variant="outline" className="border-amber-500 text-amber-600 gap-1">
              <Clock className="h-3 w-3" /> {pendingCount} pending
            </Badge>
          )}
          <Button onClick={() => { resetForm(); setShowNewDialog(true); }} className="gap-2"
            data-testid="new-transfer-btn" disabled={stores.length < 2}>
            <Plus className="h-4 w-4" /> New Transfer
          </Button>
        </div>
      </div>

      {stores.length < 2 && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <p className="text-sm text-amber-700 dark:text-amber-300">You need at least 2 stores to create transfers. Add stores from the Stores page.</p>
          </CardContent>
        </Card>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2" data-testid="transfer-filters">
        {['all', 'pending', 'approved', 'rejected'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
            data-testid={`filter-${f}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'pending' && pendingCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-amber-500 text-white rounded-full">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Transfers List */}
      <div className="space-y-3" data-testid="transfers-list">
        {filteredTransfers.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Truck className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">No transfers found</p>
              <p className="text-sm mt-1">{filter !== 'all' ? `No ${filter} transfers` : 'Create your first transfer request'}</p>
            </CardContent>
          </Card>
        )}

        {filteredTransfers.map(t => {
          const style = STATUS_STYLES[t.status] || STATUS_STYLES.pending;
          return (
            <Card key={t.id} className="hover:shadow-md transition-shadow" data-testid={`transfer-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
                        {style.label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <Store className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">{t.from_store_name}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <Store className="h-4 w-4 text-emerald-500" />
                      <span className="font-medium">{t.to_store_name}</span>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-2">
                      {t.items?.map((item, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-muted rounded">
                          {item.product_name} x{item.quantity}
                        </span>
                      ))}
                    </div>

                    {t.notes && <p className="text-xs text-muted-foreground mt-2">Note: {t.notes}</p>}
                    <p className="text-xs text-muted-foreground mt-1">By: {t.requested_by || t.transferred_by}</p>
                    {t.reviewed_by && <p className="text-xs text-muted-foreground">Reviewed by: {t.reviewed_by}</p>}
                    {t.review_notes && <p className="text-xs text-muted-foreground">Review: {t.review_notes}</p>}
                  </div>

                  {t.status === 'pending' && (
                    <Button size="sm" variant="outline" className="shrink-0"
                      onClick={() => { setReviewTransfer(t); setReviewNotes(''); setShowReviewDialog(true); }}
                      data-testid={`review-btn-${t.id}`}>
                      Review
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* New Transfer Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-blue-500" /> New Transfer Request
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From Store</Label>
                <Select value={fromStore} onValueChange={setFromStore}>
                  <SelectTrigger data-testid="from-store-select"><SelectValue placeholder="Source..." /></SelectTrigger>
                  <SelectContent>
                    {stores.filter(s => s.id !== toStore).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To Store</Label>
                <Select value={toStore} onValueChange={setToStore}>
                  <SelectTrigger data-testid="to-store-select"><SelectValue placeholder="Destination..." /></SelectTrigger>
                  <SelectContent>
                    {stores.filter(s => s.id !== fromStore).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Product Search */}
            <div>
              <Label>Add Products</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search products..." value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="pl-10" data-testid="product-search-input" />
              </div>
              {searchResults.length > 0 && (
                <div className="mt-1 border rounded-lg max-h-40 overflow-y-auto bg-card">
                  {searchResults.map(p => (
                    <button key={p.id} onClick={() => addItem(p)}
                      className="w-full flex items-center justify-between p-2 hover:bg-muted text-sm text-left border-b last:border-0">
                      <span>{p.name}</span>
                      <span className="text-xs text-muted-foreground">Stock: {p.stock_quantity || 0}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Items */}
            {transferItems.length > 0 && (
              <div className="space-y-2">
                <Label>Items to Transfer</Label>
                {transferItems.map(item => (
                  <div key={item.product_id} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                    <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 text-sm font-medium truncate">{item.product_name}</span>
                    <span className="text-xs text-muted-foreground">/{item.stock}</span>
                    <Input type="number" value={item.quantity} min={1} max={item.stock}
                      onChange={(e) => updateItemQty(item.product_id, parseInt(e.target.value) || 1)}
                      className="w-20 h-8 text-center text-sm" data-testid={`qty-${item.product_id}`} />
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500"
                      onClick={() => removeItem(item.product_id)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <Label>Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for transfer..." data-testid="transfer-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitTransfer} disabled={submitting || !fromStore || !toStore || transferItems.length === 0}
              className="gap-2" data-testid="submit-transfer-btn">
              {submitting ? 'Submitting...' : <><ArrowRightLeft className="h-4 w-4" /> Request Transfer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent data-testid="review-transfer-dialog">
          <DialogHeader>
            <DialogTitle>Review Transfer Request</DialogTitle>
          </DialogHeader>

          {reviewTransfer && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <Store className="h-4 w-4 text-blue-500" />
                <span className="font-medium">{reviewTransfer.from_store_name}</span>
                <ArrowRight className="h-4 w-4" />
                <Store className="h-4 w-4 text-emerald-500" />
                <span className="font-medium">{reviewTransfer.to_store_name}</span>
              </div>

              <div className="space-y-1">
                {reviewTransfer.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm p-2 bg-muted/50 rounded">
                    <span>{item.product_name}</span>
                    <span className="font-semibold">x{item.quantity}</span>
                  </div>
                ))}
              </div>

              {reviewTransfer.notes && (
                <p className="text-sm text-muted-foreground">Note: {reviewTransfer.notes}</p>
              )}

              <div>
                <Label>Review Notes (optional)</Label>
                <Input value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add a note..." data-testid="review-notes-input" />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleReview('reject')} disabled={reviewing}
              data-testid="reject-transfer-btn">
              <X className="h-4 w-4 mr-1" /> Reject
            </Button>
            <Button onClick={() => handleReview('approve')} disabled={reviewing}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-transfer-btn">
              <Check className="h-4 w-4 mr-1" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
